// src/pages/public/PaymentConfirmation.js
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ArrowRightIcon, 
  ClockIcon, 
  ArrowDownTrayIcon  // Changed from DownloadIcon to ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, set, get, update } from 'firebase/database';
import { verifyPayment } from '../services/paystackService';  // Fixed import path
import Swal from 'sweetalert2';

const PaymentConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reference = params.get('reference') || params.get('trxref');
    
    if (reference) {
      handleVerification(reference);
    } else if (location.state?.paymentData) {
      setPaymentDetails(location.state.paymentData);
      setStatus('success');
      savePaymentToDatabase(location.state.paymentData);
    } else {
      // Check for pending payment in sessionStorage
      const pendingPayment = sessionStorage.getItem('pendingPayment');
      if (pendingPayment) {
        const payment = JSON.parse(pendingPayment);
        setPaymentDetails(payment);
        setStatus('success');
        savePaymentToDatabase(payment);
      } else {
        setStatus('error');
      }
    }
  }, [location]);

  // Auto-redirect countdown
  useEffect(() => {
    if (status === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    } else if (status === 'success' && countdown === 0) {
      navigate('/dashboard');
    }
  }, [status, countdown, navigate]);

  const handleVerification = async (reference) => {
    try {
      const result = await verifyPayment(reference);
      
      if (result.success) {
        const paymentData = {
          reference: reference,
          amount: result.data.amount / 100,
          currency: result.data.currency,
          paidAt: result.data.paidAt,
          metadata: result.data.metadata,
          customer: result.data.customer,
        };
        
        setPaymentDetails(paymentData);
        setStatus('success');
        await savePaymentToDatabase(paymentData);
        await updateUserMembership(paymentData);
        
        // Clear pending payment from sessionStorage
        sessionStorage.removeItem('pendingPayment');
        sessionStorage.removeItem('pendingOrder');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
    }
  };

  const savePaymentToDatabase = async (paymentData) => {
    if (!currentUser) return;
    
    try {
      const paymentRef = ref(database, `payments/${paymentData.reference}`);
      await set(paymentRef, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        amount: paymentData.amount,
        paymentType: paymentData.metadata?.paymentType || paymentData.type,
        planName: paymentData.metadata?.planName || paymentData.planName,
        status: 'successful',
        reference: paymentData.reference,
        paidAt: paymentData.paidAt || new Date().toISOString(),
        createdAt: new Date().toISOString(),
        customFields: paymentData.metadata?.customFields || paymentData.customFields || {},
      });
    } catch (error) {
      console.error('Error saving payment:', error);
    }
  };

  const updateUserMembership = async (paymentData) => {
    if (!currentUser) return;
    
    const paymentType = paymentData.metadata?.paymentType || paymentData.type;
    if (paymentType === 'membership' || paymentType === 'trainer_group' || paymentType === 'trainer_personal') {
      try {
        const userRef = ref(database, `users/${currentUser.uid}`);
        const userSnap = await get(userRef);
        const currentMembership = userSnap.exists() ? userSnap.val().membership || {} : {};
        
        let expiryDate = new Date();
        let daysToAdd = 30; // Default monthly
        
        const planPeriod = paymentData.metadata?.planPeriod || paymentData.period;
        if (planPeriod) {
          switch(planPeriod) {
            case 'weekly': daysToAdd = 7; break;
            case 'monthly': daysToAdd = 30; break;
            case 'quarterly': daysToAdd = 120; break;
            case 'biannual': daysToAdd = 180; break;
            case 'yearly': daysToAdd = 365; break;
            default: daysToAdd = 30;
          }
        }
        
        expiryDate.setDate(expiryDate.getDate() + daysToAdd);
        
        await update(userRef, {
          membership: {
            ...currentMembership,
            status: 'active',
            type: paymentType,
            planName: paymentData.metadata?.planName || paymentData.planName,
            startDate: new Date().toISOString(),
            expiryDate: expiryDate.toISOString(),
            lastPaymentRef: paymentData.reference,
          },
        });

        // Show success message for trainer booking
        if (paymentType.includes('trainer')) {
          Swal.fire({
            icon: 'success',
            title: 'Trainer Session Booked!',
            html: `
              <div style="text-align: center;">
                <p>Your ${paymentType === 'trainer_personal' ? 'Personal Training' : 'Group Training'} session has been booked.</p>
                <p class="text-sm text-gray-500 mt-2">A trainer will contact you within 24 hours.</p>
              </div>
            `,
            confirmButtonColor: '#800020',
          });
        }
      } catch (error) {
        console.error('Error updating membership:', error);
      }
    }
  };

  const downloadReceipt = () => {
    const receiptHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payment Receipt - Genesis Gym</title>
        <style>
          body { font-family: 'Arial', sans-serif; padding: 40px; background: #f5f5f5; }
          .receipt { max-width: 600px; margin: 0 auto; background: white; border-radius: 16px; padding: 32px; box-shadow: 0 4px 20px rgba(0,0,0,0.1); }
          .header { text-align: center; border-bottom: 2px solid #800020; padding-bottom: 20px; margin-bottom: 20px; }
          .logo { font-size: 24px; font-weight: bold; color: #800020; }
          .title { color: #800020; font-size: 20px; margin: 10px 0; }
          .details { margin: 20px 0; }
          .row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #eee; }
          .total { font-size: 18px; font-weight: bold; color: #800020; margin-top: 20px; padding-top: 10px; border-top: 2px solid #800020; }
          .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            <div class="logo">🏋️‍♂️ GENESIS GYM</div>
            <div class="title">PAYMENT RECEIPT</div>
          </div>
          <div class="details">
            <div class="row"><strong>Reference:</strong> <span>${paymentDetails?.reference || 'N/A'}</span></div>
            <div class="row"><strong>Date:</strong> <span>${new Date(paymentDetails?.paidAt || Date.now()).toLocaleString()}</span></div>
            <div class="row"><strong>Customer:</strong> <span>${currentUser?.email || 'N/A'}</span></div>
            <div class="row"><strong>Plan:</strong> <span>${paymentDetails?.metadata?.planName || paymentDetails?.planName || 'Membership'}</span></div>
            <div class="row total"><strong>Amount Paid:</strong> <span>KES ${(paymentDetails?.amount || 0).toLocaleString()}</span></div>
          </div>
          <div class="footer">
            <p>Thank you for choosing Genesis Gym!</p>
            <p>This is a computer-generated receipt. No signature required.</p>
          </div>
        </div>
      </body>
      </html>
    `;
    
    const blob = new Blob([receiptHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt_${paymentDetails?.reference || 'payment'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-200 border-t-red-700 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Verifying your payment...</p>
          <p className="text-gray-400 text-sm mt-2">Please don't close this window</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center"
        >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircleIcon className="h-10 w-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Verification Failed</h2>
          <p className="text-gray-500 mb-6">We couldn't verify your payment. Please contact support with your transaction reference.</p>
          <div className="space-y-3">
            <Link to="/pricing" className="block w-full py-3 bg-red-700 text-white rounded-xl font-semibold hover:bg-red-800 transition">
              Try Again
            </Link>
            <Link to="/contact" className="block w-full py-3 border-2 border-gray-200 rounded-xl font-semibold hover:bg-gray-50 transition">
              Contact Support
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center px-4 py-12">
      <motion.div 
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="max-w-lg w-full bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-center">
          <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto -mt-16 mb-4 shadow-lg">
            <CheckCircleSolid className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Payment Successful!</h1>
          <p className="text-green-100">Thank you for your purchase</p>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            <div className="flex justify-between items-start">
              <span className="text-gray-500 text-sm">Transaction Reference</span>
              <span className="text-gray-800 font-mono text-xs break-all text-right max-w-[200px]">{paymentDetails?.reference}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Amount Paid</span>
              <span className="text-gray-800 font-bold text-lg">KES {paymentDetails?.amount?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Payment Date</span>
              <span className="text-gray-800 text-sm">{new Date(paymentDetails?.paidAt || Date.now()).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 text-sm">Plan / Service</span>
              <span className="text-gray-800 font-semibold text-sm text-right max-w-[200px]">
                {paymentDetails?.metadata?.planName || paymentDetails?.planName || 'Membership'}
              </span>
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-start gap-3">
              <ClockIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-800">What's Next?</p>
                <p className="text-xs text-blue-600 mt-1">
                  {paymentDetails?.metadata?.paymentType?.includes('trainer') 
                    ? 'Your trainer session has been booked. A trainer will contact you within 24 hours to confirm your schedule.'
                    : 'Your membership has been activated. You\'ll be redirected to your dashboard in a few seconds.'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button 
              onClick={downloadReceipt}
              className="flex-1 py-3 border-2 border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition flex items-center justify-center gap-2"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Receipt
            </button>
            <Link 
              to={paymentDetails?.metadata?.paymentType?.includes('trainer') ? "/dashboard" : "/dashboard"} 
              className="flex-1 py-3 bg-red-700 text-white rounded-xl font-semibold hover:bg-red-800 transition flex items-center justify-center gap-2"
            >
              Go to Dashboard
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
          
          <p className="text-center text-xs text-gray-400">
            A confirmation email has been sent to your registered email address
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default PaymentConfirmation;